import type * as React from 'react';
import { Box, IconButton, Modal as MuiModal, styled } from '@mui/material';
import type { ModalProps as MuiModalProps } from '@mui/material';
import { CloseIcon } from '../icons';
import { H3 } from '../H3';
import { Paragraph } from '../Paragraph';
import { darkModeBackgroundImageElevation } from '../../utils/darkModeBackgroundImageElevation';

const ModalRoot = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 680,
    maxHeight: 'calc(100vh - 64px)',
    width: '100%',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    backgroundImage: darkModeBackgroundImageElevation(theme, 2),
    borderRadius: 4,
    outline: 0,
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        maxWidth: 370,
    },
}));

type ModalProps = {
    header?: {
        title: React.ReactNode;
        description?: React.ReactNode;
    };
    open: boolean;
    sx?: MuiModalProps['sx'];

    /**
     * React element which acts the close button.
     * */
    closeButton?: React.ReactNode;
    closeModal?: () => void;
    className?: string;
    children?: React.ReactNode;
    // Use this if you want to override the header structure.
    // It has higher precedence the `higher` prop.
    headerEl?: React.ReactNode;
    hideCloseButton?: boolean;
    fullScreen?: boolean;
};

const Modal = ({
    header,
    open,
    closeButton,
    closeModal = () => undefined,
    className,
    children,
    sx,
    headerEl,
    fullScreen,
    hideCloseButton = false,
}: ModalProps) => {
    return (
        <MuiModal open={open} onClose={closeModal} sx={sx}>
            <ModalRoot
                className={className}
                sx={
                    fullScreen
                        ? {
                              height: '100%',
                              maxHeight: '100vh!important',
                              maxWidth: '100vw!important',
                          }
                        : undefined
                }
            >
                <Box
                    sx={{
                        height: '100%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {!hideCloseButton && (
                        <Box display="flex" justifyContent="flex-end">
                            {closeButton ? (
                                closeButton
                            ) : (
                                <IconButton onClick={closeModal} edge="end">
                                    <CloseIcon size={16} />
                                </IconButton>
                            )}
                        </Box>
                    )}
                    {headerEl
                        ? headerEl
                        : header && (
                              <Box
                                  component="header"
                                  sx={{
                                      mb: {
                                          xs: 2,
                                          sm: 3,
                                      },
                                      textAlign: 'center',
                                  }}
                              >
                                  <H3
                                      sx={{
                                          mb: {
                                              xs: 0,
                                          },
                                      }}
                                  >
                                      {' '}
                                      {header.title}
                                  </H3>
                                  {header.description && (
                                      <Paragraph color="darkGray">
                                          {header.description}
                                      </Paragraph>
                                  )}
                              </Box>
                          )}
                    <Box flex={2}>{children}</Box>
                </Box>
            </ModalRoot>
        </MuiModal>
    );
};

export default Modal;
